import { useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useRequestData } from "@hooks";
import { getFromLocalStorage } from "@utils";
import {
  signPrescriptionDocuments,
  signCompoundPrescriptionDocument,
  signDocumentOnSuccess,
} from "./index";
import {
  RequestType,
  CompoundedPrescriptionDetails,
  RequestDetail,
  DocumentCreationLoaderTitle,
} from "@interfaces";
import {
  BlockedSignatureEsignError,
  EsignError,
  InvalidPinEsignError,
} from "@interfaces";
import { useDocumentNotificationContext } from "@contexts/DocumentNotificationContext";
import { useDocumentCreationContext } from "@contexts/DocumentCreationContext";

interface UseDocumentSignatureProps {
  onCreationError?: () => void;
  onInvalidPinError?: () => void;
  onBlockedPinError?: (errorMessage: string) => void;
  onEsignError?: () => void;
  onUnknownError?: () => void;
}

/**
 * Custom hook that centralizes document signature logic for both prescription types.
 *
 * This hook handles:
 * - Signature mutations for both simple and compounded prescriptions
 * - Error handling for different signature error types
 * - Loading states and titles
 * - Navigation on successful signature
 *
 * @param requestId - The request ID to sign
 * @param onInvalidPinError - Callback for invalid PIN errors
 * @param onBlockedPinError - Callback for blocked PIN errors
 * @param onEsignError - Callback for general eSign errors
 * @param onUnknownError - Callback for unknown errors
 *
 * @returns Object containing signature functions, loading states, and error states
 */
export const useDocumentSignature = ({
  onCreationError,
  onInvalidPinError,
  onBlockedPinError,
  onEsignError,
  onUnknownError,
}: UseDocumentSignatureProps) => {
  const navigate = useNavigate();
  const { data: requestData } = useRequestData();

  const {
    isCreatingPrescription,
    isCreatingCompoundedPrescription,
    errorOnPrescription,
    errorOnCompoundedPrescription,
  } = useDocumentCreationContext();

  const requestType = getFromLocalStorage<RequestType>({
    key: "requestType",
  }) as RequestType;

  const onErrorCommonCallback = useCallback(
    (error: Error) => {
      console.error(`Error when signing documents of type ${requestType}:`, {
        error,
      });
      if (error instanceof InvalidPinEsignError) {
        onInvalidPinError?.();
      } else if (error instanceof BlockedSignatureEsignError) {
        onBlockedPinError?.(error.message);
      } else if (error instanceof EsignError) {
        onEsignError?.();
      } else {
        onUnknownError?.();
      }
    },
    [
      requestType,
      onInvalidPinError,
      onBlockedPinError,
      onEsignError,
      onUnknownError,
    ],
  );

  const { notifyPrescription } = useDocumentNotificationContext();

  // Prescription signature mutation
  const {
    mutate: signSimpleRecipe,
    isPending: isSigningPrescription,
    isError: isErrorOnSigningPrescription,
    error: errorOnSigningPrescription,
  } = useMutation({
    mutationFn: signPrescriptionDocuments,
    onSuccess: ([requestId, data]) =>
      signDocumentOnSuccess({
        requestType: RequestType.PRESCRIPTION,
        requestId,
        navigate,
        notifyPrescription,
        data,
      }),
    onError: onErrorCommonCallback,
  });

  // Compounded prescription signature mutation
  const {
    mutate: signCompoundedPrescription,
    isPending: isSigningCompoundedPrescription,
    isError: isErrorOnSigningCompoundedPrescription,
    error: errorOnSigningCompoundedPrescription,
  } = useMutation({
    mutationFn: signCompoundPrescriptionDocument,
    onSuccess: ([requestId, data]) =>
      signDocumentOnSuccess({
        requestType: RequestType.COMPOUNDED_PRESCRIPTION,
        requestId,
        navigate,
        notifyPrescription,
        data,
      }),
    onError: onErrorCommonCallback,
  });

  // Signature function that handles both types
  const signPrescription = useCallback(
    (pin: string, requestId: string) => {
      if (isCreatingPrescription || isCreatingCompoundedPrescription) {
        return;
      }

      if (errorOnPrescription || errorOnCompoundedPrescription) {
        onCreationError?.();
        return;
      }

      if (requestType === RequestType.PRESCRIPTION && requestData?.details) {
        const managementSummary = requestData?.details
          ?.map((medicine: RequestDetail) => {
            return "name" in medicine && medicine.name
              ? medicine.name
              : medicine.activePrinciple;
          })
          .join(", ");

        signSimpleRecipe({
          requestId,
          managementSummary,
          pin,
        });
      } else if (requestType === RequestType.COMPOUNDED_PRESCRIPTION) {
        const compoundedPrescriptionDetails =
          getFromLocalStorage<CompoundedPrescriptionDetails>({
            key: "compoundedPrescriptionDetails",
            parseJson: true,
          }) as CompoundedPrescriptionDetails;

        if (compoundedPrescriptionDetails) {
          signCompoundedPrescription({
            requestId,
            managementSummary:
              compoundedPrescriptionDetails.compoundedIngredients,
            pin,
          });
        }
      }
    },
    [
      requestType,
      requestData,
      isCreatingPrescription,
      isCreatingCompoundedPrescription,
      errorOnPrescription,
      errorOnCompoundedPrescription,
      onCreationError,
      signSimpleRecipe,
      signCompoundedPrescription,
    ],
  );

  // Loading states
  const isLoading = isSigningPrescription || isSigningCompoundedPrescription;
  const loadingTitle = useMemo(() => {
    if (isSigningPrescription) {
      return DocumentCreationLoaderTitle[RequestType.PRESCRIPTION];
    }
    if (isSigningCompoundedPrescription) {
      return DocumentCreationLoaderTitle[RequestType.COMPOUNDED_PRESCRIPTION];
    }
    return "";
  }, [isSigningPrescription, isSigningCompoundedPrescription]);

  return {
    signPrescription,
    isLoading,
    loadingTitle,
    isErrorOnSigningPrescription,
    errorOnSigningPrescription,
    requestType,
    isErrorOnSigningCompoundedPrescription,
    errorOnSigningCompoundedPrescription,
  };
};
